import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth is carried in an httpOnly cookie (set by the backend). Send it with every
// request via credentials; no token is stored in JS/localStorage (XSS-safe).
const api = axios.create({ baseURL: API, withCredentials: true });

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Fetch a PDF endpoint with the auth cookie and save it as a file (reliable across
// browsers vs. opening a new tab, which can be popup-blocked or miss the cookie).
export async function downloadPdf(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

// Large files (videos): let the browser stream the download directly (cookie is sent on a
// same-site navigation) instead of buffering a blob in memory, which fails for big files.
export function downloadDirect(path, filename) {
  const a = document.createElement("a");
  a.href = `${API}${path}`;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Generic authenticated file download (CSV, etc.) using the auth cookie.
export async function downloadFile(path, filename, mime) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], mime ? { type: mime } : {}));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

// Download a file using the filename the server sets in Content-Disposition
// (so the unique server-side timestamp is preserved in the saved file name).
export async function downloadServerFile(path) {
  const res = await api.get(path, { responseType: "blob" });
  const cd = res.headers["content-disposition"] || "";
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const filename = m ? decodeURIComponent(m[1]) : "report.pdf";
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

// Fetch a file and return a blob object URL + server filename (for in-app preview).
// Caller is responsible for revoking the URL when done.
export async function fetchServerFileUrl(path) {
  const res = await api.get(path, { responseType: "blob" });
  const cd = res.headers["content-disposition"] || "";
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const filename = m ? decodeURIComponent(m[1]) : "report.pdf";
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  return { url, filename };
}

export default api;
